import { assembleFiltersConfig } from "app/pages/sales-intelligence/modules/advanced-search-filtering/services/filters-registry/assemble";
import type { FilterCategories } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/category";
import type { SurfaceSelection } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterDefinition";
import type { FiltersConfig } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filtersConfig";
import { filtersConfigStatusMap } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filtersConfig";
import { normalizeCategories } from "app/pages/sales-intelligence/modules/advanced-search-filtering/utils/filterConfig";

import { leadsFilterRegistry, orderOfLeadsDefinition } from "../registry";
import type { LeadsCapability } from "../types/leadsCapability";
import {
    type LeadsDataSource,
    type LeadsFilterBuildContext,
    leadsDataSourceMap,
} from "../types/leadsFilterBuildContext";
import { type LeadsFilterCategory, leadsFilterCategoryMap } from "../types/leadsFilterCategory";
import type { OrderedFilterInstance } from "../types/leadsSearchFilterInstance";

/**
 * The leads filter list for a surface: a fold of the registry, nothing else. Every filter self-gates — via
 * `requires` (data) and the selection (category / usage / capability) — so there is no hand-curated phase
 * branch and no whole-panel readiness short-circuit: a slow or absent data source hides only its own card.
 * The one exception is the fold's `seedsDefault` gate: while a selected default-carrying filter (e.g.
 * suppression) cannot build yet, the whole surface stays `pending` with an empty list, so the one-shot
 * defaults seed (`useApplyInitialFilters`) cannot run without it.
 */
export const createLeadsSearchFiltersConfig = (
    ctx: LeadsFilterBuildContext,
    ready: ReadonlySet<LeadsDataSource>,
    selection: SurfaceSelection<LeadsFilterCategory, LeadsCapability>,
    loading?: ReadonlySet<LeadsDataSource>,
): FiltersConfig<OrderedFilterInstance[]> => {
    const desiredCategories = selection.categories;

    const isDesiredCategory = (category: FilterCategories): boolean =>
        !desiredCategories ||
        normalizeCategories(category).some((c) => desiredCategories.has(c as LeadsFilterCategory));

    const config = assembleFiltersConfig({
        registry: leadsFilterRegistry,
        selection,
        ctx,
        ready,
        loading,
        orderOf: orderOfLeadsDefinition,
    });

    if (config.status === filtersConfigStatusMap.pending) {
        return { filtersList: [], status: filtersConfigStatusMap.pending };
    }

    // `ready` stays this surface's own core-data gate rather than the fold's "every selected definition can
    // build". The two differ: the fold also waits on the industry taxonomy and the suppression list, and
    // `ScoringFiltersPanel` renders no filters at all until `ready`, so a source that resolves late — or not
    // at all — would empty that panel. Narrowing the gate to what the fold reports is a deliberate change of
    // its own, not a side effect of registering a filter.
    const isReady =
        ready.has(leadsDataSourceMap.description) &&
        (!isDesiredCategory(leadsFilterCategoryMap.website) || ready.has(leadsDataSourceMap.technologies)) &&
        (!isDesiredCategory(leadsFilterCategoryMap.signals) || ready.has(leadsDataSourceMap.signals));

    // Re-checked per instance, not just per definition: a group's members carry their own category, which
    // need not be the group's.
    return {
        status: isReady ? filtersConfigStatusMap.ready : filtersConfigStatusMap.initial,
        filtersList: (config.filtersList as readonly OrderedFilterInstance[]).filter((c) =>
            isDesiredCategory(c.category),
        ),
    };
};
